import {GetStaticPaths, GetStaticPropsContext} from 'next';

import {getAllGwgAccelerators, getGwgAccelerator} from '@udemy/career-academies';
import {disallowSlug, RENDER_MODES} from '@udemy/nextjs-core';

import {getLayout} from 'src/components/layout';
import {
    GoogleAiPlanPageFeature,
    GoogleAiPlanPageFeatureProps,
} from 'src/features/google-ai-plan-page';
import {useGetGwgPlanData} from 'src/features/google-ai-plan-page/hooks/useGetGwgPlanData';
import {useGetGrowWithGoogleEnrollmentsQuery} from 'src/generated/graphql';
import {getCompactTranslations} from 'src/lib-server/locale.server';
import {DEFAULT_REVALIDATE_SECONDS, GOOGLE_AI_ACCELERATOR_SLUG} from 'src/lib/constants';
import {getGoogleInstructorId} from 'src/lib/get-google-instructor-id';
import {getInstructorData} from 'src/lib/get-instructor-data';
import {fetcher} from 'src/lib/gql-fetcher';
import {getStaticAppContext} from 'src/pages/api/context.api';
import {AppContextData} from 'src/types/page-props';

export const getStaticPaths: GetStaticPaths = async () => {
    return {
        paths: [],
        fallback: 'blocking',
    };
};
export const getStaticProps = disallowSlug(async (context: GetStaticPropsContext) => {
    const locale = context.locale as string;
    const translations = await getCompactTranslations(locale);

    // Resolve appContext and fetch data needed to render the certificate path module.
    const [staticData, enrollmentsData, accelerator] = await Promise.all([
        getStaticAppContext({locale}),
        useGetGrowWithGoogleEnrollmentsQuery.fetcher()(),
        getGwgAccelerator(GOOGLE_AI_ACCELERATOR_SLUG, locale, {customGqlFetcher: fetcher}),
    ]);

    const instructorData = await getInstructorData({userId: getGoogleInstructorId()}, locale);

    const planData = await useGetGwgPlanData(); // NOTE: This data is hardcoded for now, we don't have an API for this yet.

    const appContext: AppContextData = {
        ...staticData,
        // application specific context can go here
    };

    const allGwgAcceleratorsData = await (
        getAllGwgAccelerators as (
            locale: string,
            options?: {customGqlFetcher?: typeof fetcher},
        ) => ReturnType<typeof getAllGwgAccelerators>
    )(locale, {
        customGqlFetcher: fetcher,
    });

    if (!allGwgAcceleratorsData) {
        return {
            notFound: true,
        };
    }

    return {
        props: {
            appContext,
            accelerator,
            enrollmentsData,
            locale,
            pageKey: 'google_ai_plan',
            renderMode: RENDER_MODES.ISR, // supports render functions in _document.tsx and _app.tsx.
            translations,
            plan: planData,
            instructor: instructorData,
            allGwgAccelerators: allGwgAcceleratorsData,
        },
        revalidate: DEFAULT_REVALIDATE_SECONDS,
    };
});

export default function GoogleAiPlanPage(props: GoogleAiPlanPageFeatureProps) {
    return <GoogleAiPlanPageFeature {...props} />;
}

GoogleAiPlanPage.getLayout = getLayout;
